import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Rocket, ArrowRight, Info } from "lucide-react";
import * as Tooltip from "@radix-ui/react-tooltip";
import { cn, getFileUrl } from "@/lib/utils";
import type { ModelSuggestion } from "../../store/streaming-store";
import type { AIModel } from "@/features/ai-model-selection/store/ai-model-store";
import { useModelDialog } from "@/features/ai-model-selection";
import { useChatAccessStore } from "@/features/chat/store/chat-access-store";
import { useCurrentChatId } from "@/hooks";
import { isModifiedClick } from "@/lib/link-utils";
import { modelsPaths } from "@/modules";

interface ModelSuggestionCardProps {
  model: ModelSuggestion | AIModel;
  isModelGrid?: boolean;
  messageId?: string; // 🆕 Mesaj listesinden gelen messageId
  isMobileCompact?: boolean; // 🆕 Mobilde dialog için kompakt horizontal layout
  isMobileChatCompact?: boolean; // 🆕 Chat akışında mobilde kompakt vertical layout
}

/**
 * ModelSuggestionCard
 * Streaming'den gelen model suggestion'ları için özel kart component'i
 * Mesaj listesinde de kullanılır
 * isMobileCompact: Mobilde dialog için kompakt horizontal layout (sol thumbnail + sağ bilgi)
 * isMobileChatCompact: Chat akışında mobilde daha küçük thumbnail, az padding, 1 satır description
 */
export const ModelSuggestionCard: React.FC<ModelSuggestionCardProps> = ({
  model,
  isModelGrid = false,
  messageId: propMessageId,
  isMobileCompact = false,
  isMobileChatCompact = false,
}) => {
  const { t, i18n } = useTranslation();
  const { closeModal } = useModelDialog();
  // 🆕 Prop'tan gelen messageId kullan (store'da messageId yok, session-based)
  const rawMessageId = propMessageId;
  // 🔧 "_assistant" veya "_user" suffix'ini kaldır
  const messageId = rawMessageId?.replace(/_(assistant|user)$/, "");
  const [mediaLoaded, setMediaLoaded] = useState(false);
  const [mediaError, setMediaError] = useState(false);
  const [thumbLoaded, setThumbLoaded] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);
  const [previewModel, setPreviewModel] = useState<
    ModelSuggestion | AIModel | null
  >(null);
  const currentLanguage = (i18n.resolvedLanguage || i18n.language) as
    | "tr"
    | "en"
    | "de"
    | "fr"
    | "it"
    | "es"
    | "ar";

  // 🔒 Shared read-only mod kontrolü
  const currentChatId = useCurrentChatId();
  const chatAccess = useChatAccessStore((state) =>
    currentChatId ? state.byChatId[currentChatId] : undefined,
  );
  const isReadOnly = chatAccess?.canEdit === false;

  // Model ID'yi al (ModelSuggestion'da id, AIModel'de _id)
  const modelId = "id" in model ? model.id : model._id;

  // Model seçildiğinde wizard sayfasına yönlendirilecek URL
  // (Link'in `to` prop'u için tıklama anından önce hesaplanır)
  const chatId = window.location.pathname.startsWith("/chat/")
    ? window.location.pathname.split("/")[2]
    : null;
  const chatIdParam = chatId ? `/${chatId}` : "";
  // 🆕 messageId varsa query parameter olarak ekle (kısaltılmış: m=)
  const modelSelectQueryParams = new URLSearchParams();
  if (messageId) {
    modelSelectQueryParams.set("m", messageId);
  }
  const modelSelectQueryString = modelSelectQueryParams.toString();
  const modelSelectUrl = `${modelsPaths.wizard(modelId)}${chatIdParam}${
    modelSelectQueryString ? `?${modelSelectQueryString}` : ""
  }`;

  // Model seçim linkine tıklanınca tetiklenen yan etkiler (analytics + grid modunda modal kapatma)
  const handleSelectModelClick = (e: React.MouseEvent) => {
    if (isReadOnly) {
      e.preventDefault(); // Shared read-only modda navigation yok
      return;
    }

    window.netmera.push((api) => {
      api.sendEvent({
        code: "pbnti",
        ea: modelId,
      });
    });

    if (isModelGrid && !isModifiedClick(e)) {
      closeModal();
    }
  };

  // Description'ı kısalt
  const description = (() => {
    if (!model.description) return "";
    if (typeof model.description === "string") return model.description;

    // Type-safe description access
    const desc = model.description as any;
    return desc[currentLanguage] || desc.en || "";
  })();

  const truncatedDescription =
    description.length > 80
      ? `${description.substring(0, 80)}...`
      : description;

  // Video detection utility
  const isVideoContent = () => {
    const outputUrl =
      (model as any).response_schema?.output_example?.output ||
      (model as any).output_object;
    if (!outputUrl) return false;
    if (typeof outputUrl !== "string") return false;
    const lowerOutput = outputUrl.toLowerCase();
    return (
      lowerOutput.includes(".mp4") ||
      lowerOutput.includes(".mov") ||
      lowerOutput.includes("video")
    );
  };

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewModel(model);
    setMobilePreviewOpen(true);
  };

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };

  const handleVideoPause = () => {
    setIsVideoPlaying(false);
  };

  const handleMediaLoad = () => {
    setMediaLoaded(true);
  };

  const handleMediaError = () => {
    setMediaError(true);
    setMediaLoaded(true);
  };

  const closeMobilePreview = () => {
    setMobilePreviewOpen(false);
    setPreviewModel(null);
  };

  const cardClasses = cn(
    "bg-bg-secondary dark:bg-gray-800 rounded-2xl transition-all duration-200",
    "border border-border-secondary dark:border-gray-700",
    "hover:border-border-primary dark:hover:border-gray-600 hover:shadow-md dark:hover:shadow-black/20",
    isMobileCompact
      ? "flex flex-row p-3 gap-3 items-center" // Dialog mobil: horizontal
      : isMobileChatCompact
        ? "flex flex-col p-2.5 gap-3 h-full" // Chat mobil: kompakt vertical
        : "flex flex-col p-3 sm:p-4 gap-4 sm:gap-5 h-full", // Normal: vertical
  );
  // Card content
  const cardContent = (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: isMobileCompact ? 1 : 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2 }}
      className={cardClasses}
    >
      {/* Thumbnail */}
      <div
        className={cn(
          "rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700 relative flex-shrink-0",
          isMobileCompact
            ? "w-20 h-20" // Dialog mobil: küçük kare thumbnail
            : isMobileChatCompact
              ? "w-full h-[120px]" // Chat mobil: daha küçük thumbnail
              : "w-full h-[160px] sm:h-[124px]", // Normal: tam genişlik
        )}
      >
        {/* Yeni model yapısında thumbnail object veya thumbnail_url */}
        {model.thumbnail?.fileUrl || (model as any).thumbnail_url ? (
          <>
            {!thumbLoaded && (
              <div className="absolute inset-0 animate-pulse bg-gray-200 dark:bg-gray-600" />
            )}
            <img
              src={
                model.thumbnail?.fileUrl
                  ? getFileUrl(model.thumbnail?.fileUrl)
                  : (model as any).thumbnail_url || ""
              }
              alt={model.title}
              className="w-full h-full object-cover transition-opacity duration-300"
              style={{ opacity: thumbLoaded ? 1 : 0 }}
              onLoad={() => setThumbLoaded(true)}
              onError={() => setThumbLoaded(true)}
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
            <Rocket className="w-12 h-12" />
          </div>
        )}

        {/* Info button - mobilde göster - output_example varsa aktif et */}
        {(model as any).response_schema?.output_example?.output && (
          <button
            className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white p-1.5 rounded-md hover:bg-black/80 transition-colors md:hidden z-10"
            onClick={handleInfoClick}
            title="Önizleme"
          >
            <Info className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Content */}
      <div
        className={cn(
          "flex flex-col min-w-0 flex-grow",
          isMobileCompact
            ? "gap-1 justify-center"
            : isMobileChatCompact
              ? "gap-2 justify-between"
              : "gap-4 sm:gap-5 justify-between",
        )}
      >
        {/* Title ve Brand Badge */}
        <div
          className={cn(
            "flex flex-col",
            isMobileCompact
              ? "gap-1"
              : isMobileChatCompact
                ? "gap-1.5"
                : "gap-2",
          )}
        >
          <h3
            className={cn(
              "text-text-primary dark:text-gray-100 font-semibold truncate min-w-0",
              isMobileCompact
                ? "text-sm leading-5"
                : isMobileChatCompact
                  ? "text-sm leading-5"
                  : "text-md leading-6",
            )}
          >
            {model.title}
          </h3>

          {/* Description - tüm modlarda göster, sadece boyutları değiştir */}
          <p
            className={cn(
              "text-text-tertiary dark:text-gray-400 font-normal leading-tight",
              isMobileCompact
                ? "text-[11px] line-clamp-2" // Dialog mobil: küçük text, 2 satır
                : isMobileChatCompact
                  ? "text-[11px] line-clamp-2" // Chat mobil: küçük text, 2 satır
                  : "text-sm leading-5 line-clamp-2", // Normal: 2 satır
            )}
          >
            {truncatedDescription}
          </p>
        </div>

        {/* Credit Bilgisi */}
        <div className="flex items-center justify-between">
          <p
            className={cn(
              "text-text-tertiary dark:text-gray-400 font-medium leading-5",
              isMobileCompact || isMobileChatCompact ? "text-xs" : "text-sm",
            )}
          >
            {model.pricing?.type === "dynamic"
              ? t("chat.modelSuggestions.dynamicPricing") || "Dynamic"
              : model.pricing?.inputTokenPrice
                ? `${model.pricing.inputTokenPrice.toLocaleString()} ${
                    t("chat.modelSuggestions.credits") || "credits"
                  }`
                : t("chat.modelSuggestions.freeModel") || "Free"}
          </p>
          {model.brand && (
            <div className="bg-gray-100 dark:bg-gray-600 rounded-sm px-1 py-0.5 flex gap-1 items-center w-fit">
              {model.brand.thumbnail?.fileUrl && (
                <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                  <img
                    src={getFileUrl(model.brand.thumbnail) || ""}
                    alt={model.brand.title || "Brand"}
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>
              )}
              <span className="text-text-secondary dark:text-gray-300 text-xs font-medium">
                {model.brand.title || "Unknown"}
              </span>
            </div>
          )}
        </div>

        {/* Select Model Button - sadece normal layout'ta göster */}
        {!isMobileCompact && (
          <Link
            to={modelSelectUrl}
            onClick={handleSelectModelClick}
            className={cn(
              "w-full bg-gradient-to-b from-[rgba(255,255,255,0.24)] to-[rgba(255,255,255,0)] bg-[#0e121b] dark:bg-gray-950 border border-white dark:border-gray-600 rounded-lg flex items-center justify-center gap-2 hover:opacity-90 transition-opacity duration-200",
              isMobileChatCompact
                ? "px-3 py-1.5 text-xs" // Chat mobil: daha küçük button
                : "px-4 py-2 sm:py-2.5",
              isReadOnly && "opacity-60 cursor-not-allowed hover:opacity-60",
            )}
            aria-disabled={isReadOnly}
          >
            <Rocket
              className={cn(
                isMobileChatCompact ? "w-3 h-3" : "w-4 h-4",
                "text-white",
              )}
            />
            <span
              className={cn(
                "text-white font-semibold",
                isMobileChatCompact ? "text-xs leading-4" : "text-sm leading-5",
              )}
            >
              {t("chat.modelSuggestions.selectModel")}
            </span>
            <ArrowRight
              className={cn(
                isMobileChatCompact ? "w-3 h-3" : "w-4 h-4",
                "text-white",
              )}
            />
          </Link>
        )}
      </div>
    </motion.div>
  );

  // Tooltip content component
  const tooltipContent = (
    <Tooltip.Content
      className="max-w-[400px] bg-black/95 backdrop-blur-sm rounded-2xl p-4 shadow-2xl border border-white/20 z-[2000] pointer-events-auto glass-enhanced"
      side="top"
      sideOffset={15}
      align="center"
      collisionPadding={15}
      onPointerDownOutside={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest(".play-button") || target.closest("video")) {
          e.preventDefault();
          return;
        }
        if (target.closest("[data-radix-tooltip-content]")) {
          e.preventDefault();
        }
      }}
    >
      <div className="relative">
        {/* Media container */}
        <div className="relative h-60 w-full rounded-xl overflow-hidden bg-gray-800/20 micro-bounce">
          {/* Loading skeleton */}
          {!mediaLoaded && !mediaError && (
            <div className="absolute inset-0 media-skeleton flex items-center justify-center">
              <div className="tooltip-spinner w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full" />
            </div>
          )}

          {/* Error state */}
          {mediaError && (
            <div className="absolute inset-0 bg-gray-800/50 flex items-center justify-center rounded-xl">
              <div className="text-center p-4">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-red-500/20 flex items-center justify-center">
                  <span className="text-red-400 text-xl">⚠</span>
                </div>
                <p className="text-white/70 text-sm">Medya yüklenemedi</p>
              </div>
            </div>
          )}

          {/* Video Content */}
          {isVideoContent() && !mediaError && (
            <>
              <video
                ref={(el) => {
                  if (el) {
                    (el as any).videoElement = el;
                  }
                }}
                className="w-full h-full object-cover rounded-xl filter-transition cursor-pointer"
                data-media-content
                loop
                playsInline
                onLoadedData={handleMediaLoad}
                onError={handleMediaError}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const video = e.currentTarget;
                  if (video.paused) {
                    video.play().catch(console.error);
                  } else {
                    video.pause();
                  }
                }}
                style={{
                  opacity: mediaLoaded ? 1 : 0,
                  transition: "opacity 0.3s ease-in-out",
                }}
              >
                <source
                  src={
                    (model as any).response_schema?.output_example?.output ||
                    (model as any).output_object
                  }
                  type="video/mp4"
                />
              </video>

              {/* Play button overlay */}
              {mediaLoaded && !isVideoPlaying && (
                <div
                  className="absolute inset-0 flex items-center justify-center"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const video = e.currentTarget.parentElement?.querySelector(
                      "video",
                    ) as HTMLVideoElement;
                    if (video) {
                      video.play().catch(console.error);
                    }
                  }}
                >
                  <div className="play-button w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center cursor-pointer hover:bg-white/30 transition-all duration-300 hover:scale-110">
                    <svg
                      className="w-8 h-8 text-white ml-1"
                      fill="white"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Image Content */}
          {!isVideoContent() && !mediaError && (
            <img
              className="w-full h-full object-cover rounded-xl filter-transition"
              data-media-content
              src={
                (model as any).response_schema?.output_example?.output ||
                (model as any).output_object
              }
              alt={model.title}
              onLoad={handleMediaLoad}
              onError={handleMediaError}
              style={{
                opacity: mediaLoaded ? 1 : 0,
                transition: "opacity 0.3s ease-in-out",
              }}
            />
          )}

          {/* Price overlay */}
          <div className="absolute top-3 right-3 price-tag bg-black/60 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-lg font-medium border border-white/10 flex items-center gap-1">
            <Rocket className="w-3 h-3" />
            {model.pricing?.type === "dynamic"
              ? t("chat.modelSuggestions.dynamicPricing") || "Dynamic"
              : model.pricing?.inputTokenPrice
                ? `${model.pricing.inputTokenPrice.toLocaleString()} ${
                    model.pricing.currency || "credits"
                  }`
                : "Ücretsiz"}
          </div>
        </div>

        {/* Content Info */}
        <div className="mt-4 text-white space-y-3">
          <div className="space-y-2">
            <h3 className="font-semibold text-base leading-tight">
              {model.title}
            </h3>
            <p className="text-sm text-white/70 leading-relaxed">
              AI model çıktısı önizleme
            </p>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-white/10">
            <span className="text-xs text-white/50 uppercase tracking-wider">
              {(model as any).metadata?.category?.name || "AI Model"}
            </span>
            <div className="flex items-center space-x-1 text-xs text-white/60">
              <span>Fiyat:</span>
              <span className="font-medium text-white/80">
                {model.pricing?.type === "dynamic"
                  ? t("chat.modelSuggestions.dynamicPricing") || "Dynamic"
                  : model.pricing?.inputTokenPrice
                    ? `${model.pricing.inputTokenPrice.toLocaleString()} ${
                        model.pricing.currency || "credits"
                      }`
                    : "Ücretsiz"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip Arrow */}
      <Tooltip.Arrow className="fill-black/95" />
    </Tooltip.Content>
  );

  // Mobile preview modal
  const mobilePreviewModal = previewModel && (
    <AnimatePresence>
      {mobilePreviewOpen && previewModel && (
        <div
          onClick={closeMobilePreview}
          className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-black/95 rounded-2xl p-4 max-w-sm w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold text-lg">
                {previewModel.title}
              </h3>
              <button
                onClick={closeMobilePreview}
                className="h-8 w-8 p-0 text-white hover:bg-white/10 rounded-md"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="relative mb-4">
              {isVideoContent() ? (
                <div className="relative group">
                  <video
                    src={
                      (previewModel as any).response_schema?.output_example
                        ?.output || (previewModel as any).output_object
                    }
                    className="w-full h-48 object-cover rounded-xl"
                    preload="metadata"
                    loop
                    playsInline
                    controls
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              ) : (
                <img
                  src={
                    (previewModel as any).response_schema?.output_example
                      ?.output || (previewModel as any).output_object
                  }
                  alt={`${previewModel.title} output örneği`}
                  className="w-full h-48 object-cover rounded-xl"
                  loading="lazy"
                />
              )}
            </div>

            {/* Info */}
            <div className="text-white">
              <div className="text-white/80 text-sm mb-2">
                {(previewModel as any).metadata?.category?.name || "AI Model"}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/70 text-sm">
                  {isVideoContent() ? "Video Örneği" : "Görsel Örneği"}
                </span>
                <span className="bg-blue-500/20 text-blue-300 text-xs px-2 py-1 rounded-full">
                  {previewModel.pricing?.type === "dynamic"
                    ? t("chat.modelSuggestions.dynamicPricing") || "Dynamic"
                    : previewModel.pricing?.inputTokenPrice
                      ? `${previewModel.pricing.inputTokenPrice.toLocaleString()} ${
                          previewModel.pricing.currency || "credits"
                        }`
                      : "Ücretsiz"}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  // Tooltip'i aktif et - output_example kontrolü ile
  if ((model as any).response_schema?.output_example?.output) {
    return (
      <>
        {/* Desktop: Tooltip */}
        <div className="hidden md:block h-full">
          <Tooltip.Root>
            <Tooltip.Trigger asChild>{cardContent}</Tooltip.Trigger>
            <Tooltip.Portal>{tooltipContent}</Tooltip.Portal>
          </Tooltip.Root>
        </div>

        {/* Mobile: Sadece kart (info button ile) */}
        <div className="md:hidden h-full">{cardContent}</div>

        {/* Mobile Preview Modal */}
        {mobilePreviewModal}
      </>
    );
  }

  // Output object yoksa sadece kart
  return <div className="h-full">{cardContent}</div>;
};
